import {HttpRequest} from "@angular/common/http";

export const BYTES_PER_MB = 1048576;
export const MAX_FILE_SIZE_MB = 25;
// New files go to the server in a single multipart request, and Cloudflare (Free plan) rejects request bodies over 100MB.
// Keep headroom for the multipart overhead and the JSON part of the request.
export const MAX_UPLOAD_TOTAL_MB = 90;

export const fileSizeErrorMessage = (maxFileSizeMb: number = MAX_FILE_SIZE_MB) =>
  `Error: Unable to upload. The file size must not exceed ${maxFileSizeMb}MB.`;
export const TOTAL_UPLOAD_SIZE_ERROR_MESSAGE =
  `Error: Unable to upload. The total size of new files must not exceed ${MAX_UPLOAD_TOTAL_MB}MB. Please save and add the remaining files separately.`;
export const UPLOAD_FAILED_NOTIFICATION =
  'Error. Unable to upload. The connection was interrupted, please check your connection and try again';
export const UPLOAD_TOO_LARGE_NOTIFICATION =
  `Error. Unable to upload. The total size of files uploaded at once must not exceed ${MAX_UPLOAD_TOTAL_MB}MB`;

export function sumFileSizes(files: { sizeBytes?: number }[] | null | undefined): number {
  return (files || []).reduce((sum, file) => sum + (file.sizeBytes || 0), 0);
}

/**
 * Splits the selected files into the ones that can be added to the pending upload and an error message for the rest.
 * pendingBytes is the size of new files already waiting to be sent in the same request.
 */
export function pickUploadableFiles(files: ArrayLike<File> | Iterable<File>,
                                    pendingBytes: number,
                                    maxFileSizeMb: number = MAX_FILE_SIZE_MB): { accepted: File[], error: string | null } {
  const accepted: File[] = [];
  let totalBytes = pendingBytes;
  let fileTooLarge = false;
  let totalTooLarge = false;

  for (const file of Array.from(files)) {
    if (file.size > maxFileSizeMb * BYTES_PER_MB) {
      fileTooLarge = true;
      continue;
    }

    if (totalBytes + file.size > MAX_UPLOAD_TOTAL_MB * BYTES_PER_MB) {
      totalTooLarge = true;
      continue;
    }

    accepted.push(file);
    totalBytes += file.size;
  }

  const error = totalTooLarge ? TOTAL_UPLOAD_SIZE_ERROR_MESSAGE : fileTooLarge ? fileSizeErrorMessage(maxFileSizeMb) : null;
  return {accepted, error};
}

export function isFileUploadRequest(request: HttpRequest<unknown>): boolean {
  if (!(request.body instanceof FormData)) return false;

  let hasFile = false;
  request.body.forEach(value => hasFile ||= value instanceof File);
  return hasFile;
}
